import { useState, useRef, useCallback, useEffect } from 'react';
import useDragWidth from './useDragWidth';
import useDragLength from './useDragLength';

/**
 * Custom hook for managing split view state and behavior
 * @param {Object} options - Configuration options
 * @returns {Object} Split view state and handlers
 */
export default function useSplitView(options = {}) {
  const {
    onTabSelect,
    onTabClose,
    openFiles = [],
    activeFileId = null
  } = options;

  // Split view state
  const [splitMode, setSplitMode] = useState(null);
  const [splitPrimaryFileId, setSplitPrimaryFileId] = useState(null);
  const [splitSecondaryActiveId, setSplitSecondaryActiveId] = useState(null);

  // Split panel refs for dragging
  const topPanelRef = useRef(null);
  const bottomPanelRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Initialize drag handlers for both split modes
  const { handleMouseDown: handleHorizontalDrag } = useDragWidth(leftPanelRef, {
    defaultWidth: window.innerWidth * 0.5,
    minWidth: 200,
    maxWidth: window.innerWidth - 200,
    invertDrag: false
  });

  const { handleMouseDown: handleVerticalDrag } = useDragLength(topPanelRef, {
    defaultHeight: window.innerHeight * 0.5,
    minHeight: 200,
    maxHeight: window.innerHeight - 200,
    invertDrag: false
  });

  /**
   * Handle tab selection based on split mode
   */
  const handleSelectTab = useCallback((fileId) => {
    // If not in split mode, normal tab selection
    if (!splitMode) {
      onTabSelect(fileId);
    } else {
      // If we are in split mode, see if it's the primary file or belongs to secondary
      if (fileId === splitPrimaryFileId) {
        // Primary file is already set, no change needed
      } else {
        // It's in the secondary set
        setSplitSecondaryActiveId(fileId);
      }
    }
  }, [splitMode, splitPrimaryFileId, onTabSelect]);

  /**
   * Handle tab closing based on split mode
   */
  const handleCloseTab = useCallback((e, fileId) => {
    e.stopPropagation();

    // Always close the tab first
    onTabClose(fileId);

    // If not in split mode, just normal close behavior
    if (!splitMode) {
      return;
    }

    // If in split mode, handle the split state appropriately
    if (fileId === splitPrimaryFileId) {
      // Closing the primary file - try to find another file to be primary
      const remainingFiles = openFiles.filter(f => f.id !== fileId);
      
      if (remainingFiles.length === 0) {
        // No files left at all, exit split mode
        setSplitMode(null);
        setSplitPrimaryFileId(null);
        setSplitSecondaryActiveId(null);
      } else if (remainingFiles.length === 1) {
        // Only one file left, exit split mode and make it the active file
        setSplitMode(null);
        setSplitPrimaryFileId(null);
        setSplitSecondaryActiveId(null);
        onTabSelect(remainingFiles[0].id);
      } else {
        // Multiple files left, promote the secondary active to primary (or pick first remaining)
        const newPrimary = splitSecondaryActiveId && remainingFiles.find(f => f.id === splitSecondaryActiveId)
          ? splitSecondaryActiveId
          : remainingFiles[0].id;
        
        setSplitPrimaryFileId(newPrimary);
        
        // Set new secondary active (first file that's not the new primary)
        const newSecondary = remainingFiles.find(f => f.id !== newPrimary);
        setSplitSecondaryActiveId(newSecondary ? newSecondary.id : null);
      }
    } else if (fileId === splitSecondaryActiveId) {
      // Closing the secondary active file
      const remainingSecondary = openFiles.filter(
        f => f.id !== splitPrimaryFileId && f.id !== fileId
      );
      
      if (remainingSecondary.length > 0) {
        // Set the next secondary file as active
        setSplitSecondaryActiveId(remainingSecondary[0].id);
      } else {
        // No more secondary files, but keep primary and stay in split mode
        // The secondary panel will show empty state
        setSplitSecondaryActiveId(null);
      }
    }
    // If it's neither primary nor secondary active, it's just a background tab being closed
    // No split mode changes needed
  }, [splitMode, splitPrimaryFileId, splitSecondaryActiveId, openFiles, onTabClose, onTabSelect]);

  /**
   * Split view vertically (top/bottom)
   */
  const handleSplitVertical = useCallback(() => {
    if (openFiles.length === 0) return;
    
    // Get the active file or the first file if no active file
    const fileToSplit = openFiles.find(f => f.id === activeFileId) || openFiles[0];
    if (!fileToSplit) return;
    
    setSplitMode('vertical');
    setSplitPrimaryFileId(fileToSplit.id);

    // Among the open files, pick the first "other" one for secondary (if exists)
    const others = openFiles.filter(f => f.id !== fileToSplit.id);
    if (others.length > 0) {
      setSplitSecondaryActiveId(others[0].id);
    } else {
      setSplitSecondaryActiveId(null);
    }
  }, [openFiles, activeFileId]);

  /**
   * Split view horizontally (left/right)
   */
  const handleSplitHorizontal = useCallback(() => {
    if (openFiles.length === 0) return;
    
    // Get the active file or the first file if no active file
    const fileToSplit = openFiles.find(f => f.id === activeFileId) || openFiles[0];
    if (!fileToSplit) return;
    
    setSplitMode('horizontal');
    setSplitPrimaryFileId(fileToSplit.id);

    // Among the open files, pick the first "other" one for secondary (if exists)
    const others = openFiles.filter(f => f.id !== fileToSplit.id);
    if (others.length > 0) {
      setSplitSecondaryActiveId(others[0].id);
    } else {
      setSplitSecondaryActiveId(null);
    }
  }, [openFiles, activeFileId]);

  /**
   * Exit split view mode
   */
  const handleExitSplit = useCallback(() => {
    setSplitMode(null);
    setSplitPrimaryFileId(null);
    setSplitSecondaryActiveId(null);
  }, []);

  /**
   * Get the secondary files (all files except primary)
   */
  const getSecondaryFiles = useCallback(() => {
    return openFiles.filter(f => f.id !== splitPrimaryFileId);
  }, [openFiles, splitPrimaryFileId]);

  /**
   * Get the primary file object
   */
  const getPrimaryFile = useCallback(() => {
    return openFiles.find(f => f.id === splitPrimaryFileId);
  }, [openFiles, splitPrimaryFileId]);

  /**
   * Get the active secondary file object
   */
  const getSecondaryFile = useCallback(() => {
    return openFiles.find(f => f.id === splitSecondaryActiveId);
  }, [openFiles, splitSecondaryActiveId]);

  // Ensure secondaryActiveId is always set if there are secondary files
  useEffect(() => {
    if (
      splitMode &&
      openFiles.length > 1 &&
      splitPrimaryFileId &&
      !splitSecondaryActiveId
    ) {
      const secondary = openFiles.find(f => f.id !== splitPrimaryFileId);
      if (secondary) {
        setSplitSecondaryActiveId(secondary.id);
      }
    }
  }, [splitMode, openFiles, splitPrimaryFileId, splitSecondaryActiveId]);

  return {
    // State
    splitMode,
    splitPrimaryFileId,
    splitSecondaryActiveId,
    
    // Element refs
    topPanelRef,
    bottomPanelRef,
    leftPanelRef,
    rightPanelRef,
    
    // Drag handlers
    handleVerticalDrag,
    handleHorizontalDrag,
    
    // Action handlers
    handleSelectTab,
    handleCloseTab,
    handleSplitVertical,
    handleSplitHorizontal,
    handleExitSplit,
    
    // Helper functions
    getSecondaryFiles,
    getPrimaryFile,
    getSecondaryFile
  };
} 